import {SongSearchService} from './song-search-service';
import {debounce} from "../../../shared/services/utils";
import {
  EditorFileChangedChannel,
  FileDeletedChannel,
  FileRenamedChannel,
  FileSavedToDatabaseChannel,
  SearchIndexRebuiltChannel,
  UIRefreshFilesChannel
} from "../../../infrastructure/events/channels";
import yjsDocumentService from "../../../infrastructure/sync/YjsDocumentService";
import {getWorkspaceId} from "../../../shared/services/dbUtils";
import {restoreSongLibraryState, validateLibraryState} from './song-library-store';
import {assertWorkspaceWritable} from '../../../shared/services/workspaceAccess';


// Create channels
const channels = {
  searchIndexRebuilt: new SearchIndexRebuiltChannel(),
  fileSavedToDatabase: new FileSavedToDatabaseChannel(),
  uiRefreshFiles: new UIRefreshFilesChannel(),
  editorFileChanged: null,
  fileDeleted: null,
  fileRenamed: null,
  sessionUpdated: null
};

let monitoredWorkspace = null;
let stopFileList = null;
const documentSubscriptions = new Map();
let listSignature = '';
const monitorFiles = (workspaceId, names) => {
  if (workspaceId !== getWorkspaceId()) return;
  if (monitoredWorkspace !== workspaceId) {
    stopFileList?.();
    documentSubscriptions.forEach(stop => stop?.());
    documentSubscriptions.clear();
    monitoredWorkspace = workspaceId;
    listSignature = JSON.stringify(names.slice().sort());
    stopFileList = yjsDocumentService.subscribeToFileList(workspaceId, files => {
      const signature = JSON.stringify(files.slice().sort());
      if (signature !== listSignature) {
        listSignature = signature;
        songSearch.markDirty(workspaceId);
        monitorFiles(workspaceId, files);
        if (!songSearch.workspace(workspaceId).documents.length && files.length) debouncedReindex();
      }
    });
  }
  documentSubscriptions.forEach((stop, name) => {
    if (!names.includes(name)) { stop?.(); documentSubscriptions.delete(name); }
  });
  names.forEach(name => {
    if (documentSubscriptions.has(name)) return;
    const stop = yjsDocumentService.subscribeToTextChanges(workspaceId, name, content => {
      const cached = songSearch.workspace(workspaceId).cache.get(name);
      if (cached && cached.content !== content) songSearch.markDirty(workspaceId);
    });
    documentSubscriptions.set(name, stop);
  });
};

const songSearch = new SongSearchService({
  readFiles: id => yjsDocumentService.getFileList(id),
  readContent: (id, name) => yjsDocumentService.getDocumentContent(id, name),
  onWorkspace: monitorFiles,
  onRebuilt: (workspaceId, fileCount) => {
    channels.searchIndexRebuilt.postMessage({__source: 'db', workspaceId, fileCount});
    channels.fileSavedToDatabase.postMessage({action: 'indexRebuilt', workspaceId, fileCount});
  }
});
export const getSongSearchState = () => songSearch.state(getWorkspaceId());
export const subscribeSongSearchState = listener => songSearch.subscribe(id => {
  if (id === getWorkspaceId()) listener();
});
export const rebuildSearchIndex = () => songSearch.rebuild(getWorkspaceId());
const debouncedReindex = debounce(() => rebuildSearchIndex(), 100);
const broadcastFileChange = (action, fileName, oldFileName = null) => {
  channels.uiRefreshFiles.postMessage({action, fileName, oldFileName, timestamp: new Date().toISOString(), uiOnly: true});
};

// File operations
export const deleteFile = async (fileName) => {
  try {
    const workspaceId = getWorkspaceId();

    // Use deleteFileFromList which handles both the file list and content
    yjsDocumentService.deleteFileFromList(workspaceId, fileName);
    songSearch.markDirty(workspaceId);

    console.log(`File ${fileName} deleted from YJS storage`);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
};

export const renameFile = async (oldFileName, newFileName) => {
  try {
    console.log(`renameFile: Attempting to rename "${oldFileName}" to "${newFileName}"`);

    if (!newFileName || typeof newFileName !== 'string') {
      console.error('Invalid new filename for rename operation:', newFileName);
      return false;
    }

    const workspaceId = getWorkspaceId();
    if (process.env.REACT_APP_HOSANNA_DIRECT === '1') {
      const renamed = yjsDocumentService.renameFile(workspaceId, oldFileName, newFileName);
      songSearch.markDirty(workspaceId); return renamed;
    }
    const content = await fetchFileContent(oldFileName);

    if (content === null) {
      console.error(`File ${oldFileName} not found for rename operation`);
      return false;
    }

    // Update document with new file name
    yjsDocumentService.updateDocument(workspaceId, content, newFileName);

    // Delete the old file
    yjsDocumentService.deleteFileFromList(workspaceId, oldFileName);
    songSearch.markDirty(workspaceId);

    console.log(`YJS: File renamed from "${oldFileName}" to "${newFileName}"`);
    return true;
  } catch (error) {
    console.error('Error renaming file:', error);
    return false;
  }
};

const clearAllSongs = async () => {
  try {
    const workspaceId = getWorkspaceId();
    const fileNames = await listAllFiles();

    // Delete each file from the workspace
    for (const fileName of fileNames) {
      yjsDocumentService.deleteFileFromList(workspaceId, fileName);
      songSearch.markDirty(workspaceId);
    }

    console.log('All songs cleared from YJS storage');
    return true;
  } catch (error) {
    console.error('Error clearing songs:', error);
    return false;
  }
};

const importSongs = async (songFiles) => {
  try {
    const workspaceId = getWorkspaceId();

    for (const file of songFiles) {
      console.log(`Importing file: ${file.name}`);
      yjsDocumentService.updateDocument(workspaceId, file.content, file.name);
      songSearch.markDirty(workspaceId);
    }

    console.log(`${songFiles.length} songs imported to YJS storage`);
    return true;
  } catch (error) {
    console.error('Error importing songs:', error);
    return false;
  }
};

// Message handlers
const handleFileMessage = async (message) => {
  try {
    console.log('Processing file message:', message);

    if (message.action === 'delete') {
      if (!message.fileName) throw new Error('Missing fileName for delete operation');
      const success = await deleteFile(message.fileName);
      if (success && !message._preventBroadcast)
        broadcastFileChange('delete', message.fileName);
    }
    else if (message.action === 'rename') {
      const oldFileName = message.oldFileName;
      const newFileName = message.newFileName || message.fileName;

      if (!oldFileName || !newFileName)
        throw new Error('Missing oldFileName or fileName for rename operation');

      console.log(`Renaming file from '${oldFileName}' to '${newFileName}'`);
      const success = await renameFile(oldFileName, newFileName);

      if (success && !message._preventBroadcast)
        broadcastFileChange('rename', newFileName, oldFileName);
      else if (!success)
        console.error('Rename operation failed');
    }
    else {
      if (!message.fileName) throw new Error('Missing fileName for file operation');

      const workspaceId = getWorkspaceId();
      yjsDocumentService.updateDocument(
        workspaceId,
        message.fileContent || '',
        message.fileName
      );
      songSearch.markDirty(workspaceId);

      console.log(`File ${message.fileName} updated/saved to YJS storage`);
      if (!message._preventBroadcast) broadcastFileChange('edit', message.fileName);
    }

  } catch (error) {
    console.error('Error handling file message:', error, 'Original message:', message);
  }
};

// Initialize event channels
channels.editorFileChanged = new EditorFileChangedChannel(async message => {
  await handleFileMessage(message.data);
});

channels.fileDeleted = new FileDeletedChannel(async message => {
  console.log('DB received file delete event:', message.data);
  if (message.data.__source === 'ws') {
    await handleFileMessage({...message.data, _preventBroadcast: true});
    channels.uiRefreshFiles.postMessage({
      action: 'delete', fileName: message.data.fileName, uiOnly: true
    });
  } else await handleFileMessage(message.data);
});

channels.fileRenamed = new FileRenamedChannel(async message => {
  console.log('DB received file rename event:', message.data);
  try {
    if (!message.data) {
      console.error('Invalid message received in fileRenamedChannel:', message);
      return;
    }

    if (!message.data.oldFileName) {
      console.error('Missing oldFileName in rename event:', message.data);
      return;
    }

    const newFileName = message.data.newFileName || message.data.fileName;
    if (!newFileName) {
      console.error('Missing fileName/newFileName in rename event:', message.data);
      return;
    }

    const standardizedMessage = {
      ...message.data, fileName: newFileName, newFileName, action: 'rename'
    };

    if (message.data.__source === 'ws') {
      await handleFileMessage({...standardizedMessage, _preventBroadcast: true});
      channels.uiRefreshFiles.postMessage({
        action: 'rename', oldFileName: message.data.oldFileName, newFileName, uiOnly: true
      });
    } else await handleFileMessage(standardizedMessage);
  } catch (error) {
    console.error('Error in fileRenamedChannel:', error);
  }
});

// The boolean parameter remains compatible with existing search callers.
export const searchForSongs = (query, forceReindex = false) => {
  const id = getWorkspaceId();
  if (forceReindex || songSearch.state(id).status === 'missing') songSearch.rebuild(id);
  try { return songSearch.search(id, query || ''); }
  catch (error) { console.error('Error searching files:', error); return []; }
};

export const fetchFileContent = async fileName => {
  try {
    const workspaceId = getWorkspaceId();
    return await yjsDocumentService.getDocumentContent(workspaceId, fileName);
  } catch (error) {
    console.error(`Error fetching content for file ${fileName}:`, error);
    return null;
  }
};

export const listAllFiles = async () => {
  try {
    const workspaceId = getWorkspaceId();
    return await yjsDocumentService.getFileList(workspaceId);
  } catch (error) {
    console.error('Error listing files:', error);
    return [];
  }
};

export const clearAllSongsAndImport = async songFiles => {
  assertWorkspaceWritable(getWorkspaceId());
  if (songFiles.libraryState) validateLibraryState(songFiles.libraryState);
  await clearAllSongs();
  await importSongs(songFiles);
  // Legacy TXT-only backups leave personal metadata intact.
  if (songFiles.libraryState) restoreSongLibraryState(getWorkspaceId(), songFiles.libraryState);
  return true;
};

window.addEventListener('load', () => rebuildSearchIndex());
// Delay initial work so the first application render can complete.
debouncedReindex();

// Placeholder component for compatibility
const DBComponent = () => null;
export default DBComponent;
